import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useApp } from '../contexts/AppContext';

// Componentes de autenticación
import LoginForm from './auth/LoginForm';
import LoadingSpinner from './common/LoadingSpinner';

// Layout principal
import MainLayout from './common/MainLayout';

// Páginas principales
import Inicio from './inicio/Inicio';
import ProjectList from './projects/ProjectList';
import ProjectDetail from './projects/ProjectDetail';
import ProjectConfig from './projects/ProjectConfig';
import SubjectEvaluationConfig from './projects/SubjectEvaluationConfig';
import SpecialEvaluationConfig from './projects/SpecialEvaluationConfig';
import ProjectEvaluationExecution from './projects/ProjectEvaluationExecution';
import SpecialEvaluationExecution from './projects/SpecialEvaluationExecution';
import UserList from './users/UserList';
import UserDetail from './users/UserDetail';
import StudentList from './students/StudentList';
import StudentDetail from './students/StudentDetail';
import CourseList from './courses/CourseList';
import CourseDetail from './courses/CourseDetail';
import EvaluationList from './evaluations/EvaluationList';
import CalendarView from './calendar/CalendarView';
import MessagesList from './messages/MessagesList';
import FinancesList from './finances/FinancesList';
import ReportsList from './reports/ReportsList';
import AdminPage from './admin/AdminPage';
import UserProfile from './profile/UserProfile';
import GeneralSettings from './admin/GeneralSettings';
import RolesPermissions from './admin/RolesPermissions';
import LearningObjectives from './admin/LearningObjectives';
import EvaluationIndicators from './admin/EvaluationIndicators';
import AdminLayout from './admin/AdminLayout';

// Componente para rutas protegidas
const ProtectedRoute = ({ children, requiredRoles = [], requiredPermissions = [] }) => {
  const { user, isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (!isAuthenticated || !user) {
    return <Navigate to="/login" replace />;
  }

  // Verificar roles si se especifican
  if (requiredRoles.length > 0) {
    const hasRole = requiredRoles.includes(user.role);
    if (!hasRole) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  // Verificar permisos si se especifican
  if (requiredPermissions.length > 0) {
    const hasPermission = requiredPermissions.some(permission => 
      user.permissions?.includes(permission) || user.permissions?.includes('all')
    );
    if (!hasPermission) {
      return <Navigate to="/unauthorized" replace />;
    }
  }

  return children;
};

// Componente para páginas públicas (solo accesibles si NO está autenticado)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  if (isAuthenticated) {
    return <Navigate to="/home" replace />;
  }

  return children;
};

// Componente de página no autorizada
const UnauthorizedPage = () => {
  const { user } = useAuth();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.5 0L4.732 15.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Acceso Denegado</h1>
        <p className="text-gray-600 mb-6">
          No tienes permisos suficientes para acceder a esta página.
        </p>
        <div className="space-y-3">
          <div className="text-sm text-gray-500">
            Conectado como: <span className="font-medium">{user?.name}</span>
          </div>
          <div className="text-sm text-gray-500">
            Rol: <span className="font-medium capitalize">{user?.role}</span>
          </div>
        </div>
        <div className="mt-6">
          <button
            onClick={() => window.history.back()}
            className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Volver
          </button>
        </div>
      </div>
    </div>
  );
};

// Componente de página no encontrada
const NotFoundPage = () => {
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-8 text-center">
        <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
          <svg className="w-8 h-8 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 12h6m-6-4h6m2 5.291A7.962 7.962 0 0112 15c-2.137 0-4.146-.832-5.657-2.343" />
          </svg>
        </div>
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Página No Encontrada</h1>
        <p className="text-gray-600 mb-6">
          La página que buscas no existe o ha sido movida.
        </p>
        <div className="mt-6 space-x-3">
          <button
            onClick={() => window.history.back()}
            className="bg-gray-500 hover:bg-gray-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Volver
          </button>
          <button
            onClick={() => window.location.href = '/home'}
            className="bg-primary-500 hover:bg-primary-600 text-white font-medium py-2 px-4 rounded-lg transition-colors"
          >
            Ir al Inicio
          </button>
        </div>
      </div>
    </div>
  );
};

const AppRouter = () => {
  const { isLoading } = useAuth();

  // Mostrar loading global mientras se inicializa la autenticación
  if (isLoading) {
    return <LoadingSpinner fullScreen />;
  }

  return (
    <Routes>
      {/* Rutas públicas */}
      <Route
        path="/login"
        element={
          <PublicRoute>
            <LoginForm />
          </PublicRoute>
        }
      />

      {/* Ruta raíz - redirige al inicio si está autenticado */}
      <Route path="/" element={<Navigate to="/home" replace />} />

      {/* Rutas protegidas con layout */}
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <MainLayout>
              <Routes>
                {/* Inicio - Accesible para todos los roles autenticados */}
                <Route path="/home" element={<Inicio />} />

                {/* Perfil de usuario - Accesible para todos los roles autenticados */}
                <Route path="/profile" element={<UserProfile />} />

                {/* Proyectos ABP - Accesible para profesores, gestores y administradores */}
                <Route
                  path="/projects"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <ProjectList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:id"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <ProjectDetail />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:id/config"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <ProjectConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/subjects/:subjectId/evaluation"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <SubjectEvaluationConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/subjects/:subjectId/execute"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <ProjectEvaluationExecution />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/subjects/:subjectId/:evaluationType/config"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <SpecialEvaluationConfig />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/projects/:projectId/subjects/:subjectId/:evaluationType/execute"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'all']}>
                      <SpecialEvaluationExecution />
                    </ProtectedRoute>
                  }
                />

                {/* Usuarios - Solo administradores y gestores */}
                <Route
                  path="/users"
                  element={
                    <ProtectedRoute requiredPermissions={['users', 'all']}>
                      <UserList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/users/:id"
                  element={
                    <ProtectedRoute requiredPermissions={['users', 'all']}>
                      <UserDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Cursos y Materias - Administradores, gestores y profesores */}
                <Route
                  path="/courses"
                  element={
                    <ProtectedRoute requiredPermissions={['courses', 'all']}>
                      <CourseList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/courses/:id"
                  element={
                    <ProtectedRoute requiredPermissions={['courses', 'all']}>
                      <CourseDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Alumnos - Administradores, gestores y profesores */}
                <Route
                  path="/students"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'courses', 'all']}>
                      <StudentList />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/students/:id"
                  element={
                    <ProtectedRoute requiredPermissions={['projects', 'courses', 'all']}>
                      <StudentDetail />
                    </ProtectedRoute>
                  }
                />

                {/* Evaluaciones - Profesores, gestores y administradores */}
                <Route
                  path="/evaluations"
                  element={
                    <ProtectedRoute requiredPermissions={['evaluations', 'all']}>
                      <EvaluationList />
                    </ProtectedRoute>
                  }
                />

                {/* Notas - Estudiantes, profesores, apoderados, gestores y administradores */}
                <Route
                  path="/grades"
                  element={
                    <ProtectedRoute requiredPermissions={['grades', 'all']}>
                      <div>Mis Notas - En construcción</div>
                    </ProtectedRoute>
                  }
                />

                {/* Calendario - Accesible según permisos */}
                <Route
                  path="/calendar"
                  element={
                    <ProtectedRoute requiredPermissions={['calendar', 'all']}>
                      <CalendarView />
                    </ProtectedRoute>
                  }
                />

                {/* Mensajería - Accesible según permisos */}
                <Route
                  path="/messages"
                  element={
                    <ProtectedRoute requiredPermissions={['messages', 'all']}>
                      <MessagesList />
                    </ProtectedRoute>
                  }
                />

                {/* Finanzas - Solo administradores, gestores y apoderados */}
                <Route
                  path="/finances"
                  element={
                    <ProtectedRoute requiredPermissions={['payments', 'all']}>
                      <FinancesList />
                    </ProtectedRoute>
                  }
                />

                {/* Reportes - Administradores, gestores y apoderados */}
                <Route
                  path="/reports"
                  element={
                    <ProtectedRoute requiredPermissions={['reports', 'all']}>
                      <ReportsList />
                    </ProtectedRoute>
                  }
                />

                {/* Administración - Solo administradores */}
                <Route
                  path="/admin"
                  element={
                    <ProtectedRoute requiredPermissions={['all']}>
                      <Navigate to="/admin/general" replace />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/general"
                  element={
                    <ProtectedRoute requiredPermissions={['all']}>
                      <AdminLayout>
                        <GeneralSettings />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/users"
                  element={
                    <ProtectedRoute requiredPermissions={['all']}>
                      <AdminLayout>
                        <UserList />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/roles"
                  element={
                    <ProtectedRoute requiredPermissions={['all']}>
                      <AdminLayout>
                        <RolesPermissions />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/objectives"
                  element={
                    <ProtectedRoute requiredPermissions={['all']}>
                      <AdminLayout>
                        <LearningObjectives />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/admin/indicators"
                  element={
                    <ProtectedRoute requiredPermissions={['all']}>
                      <AdminLayout>
                        <EvaluationIndicators />
                      </AdminLayout>
                    </ProtectedRoute>
                  }
                />

                {/* Páginas de error */}
                <Route path="/unauthorized" element={<UnauthorizedPage />} />
                <Route path="*" element={<NotFoundPage />} />
              </Routes>
            </MainLayout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

export default AppRouter;